import * as Yup from 'yup';
import { Formik } from 'formik';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ErrorIcon from '@mui/icons-material/Error';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GroupGeneralForm } from 'user/components/GroupForm/GroupGeneralForm';
import { Tab, Tabs } from 'common/components/Tabs';
import { IFeature, IGroupFragment } from 'graphql/types';
import { Button } from 'common/components/Button';
import { FormDeleteButton } from 'common/components/Button/FormDeleteButton';
import { useFrontendPermissions } from 'user/hooks';
import { useFeature } from 'common/hooks/useFeature';
import { ExitPageAlert } from 'common/components/ExitPageAlert';
import { UnsavedChangesAlert } from 'common/components/Alerts';
import {
    EGroupFormTab,
    TGroupCreateFormValues,
    TGroupUpdateFormValues,
} from 'user/types';

import { ManagersView } from './ManagersView';
import { SkillsView } from './SkillsView';
import { UsersView } from './UsersView';

interface IProps {
    disabled?: boolean;
    group?: IGroupFragment;
    initialTab?: EGroupFormTab;
    isEdit?: boolean;
    isEditable?: boolean;
    initialValues: TGroupCreateFormValues | TGroupUpdateFormValues;
    onClose?(): void;
    onDelete?(): void;
    onSubmit(values: TGroupCreateFormValues | TGroupUpdateFormValues): void;
    onUserUpdate?(): void;
}

export const GroupForm = ({
    disabled,
    group,
    initialTab,
    isEdit,
    isEditable = true,
    onClose,
    onDelete,
    onSubmit,
    onUserUpdate,
    ...other
}: IProps) => {
    const [translate] = useTranslation();
    const { canUpdate: canUpdateGroup } = useFrontendPermissions('group');
    const { canUse: canUseSkills } = useFeature(IFeature.Skill);
    const showSkillsTab =
        canUseSkills && (group?.skillRequirements?.length ?? 0) > 0;

    // Fall back to the participants tab when the skills tab is requested but
    // not available, otherwise the form would render with no tab selected.
    const resolvedInitialTab =
        initialTab === EGroupFormTab.Skills && !showSkillsTab
            ? EGroupFormTab.Participants
            : (initialTab ??
              (isEdit ? EGroupFormTab.Participants : EGroupFormTab.General));
    const [activeTab, setActiveTab] = useState<number>(resolvedInitialTab);

    const skillRequirementSchema = Yup.object().shape({
        minAbsolute: Yup.number()
            .typeError(translate('validate.mustBeANumber'))
            .integer(translate('validate.integer'))
            .min(0, translate('validate.zeroOrPositive'))
            .nullable(),
        minPercent: Yup.number()
            .typeError(translate('validate.mustBeANumber'))
            .integer(translate('validate.integer'))
            .min(0, translate('validate.zeroOrPositive'))
            .max(100, translate('validate.max', { max: 100 }))
            .nullable(),
    });

    const validationSchema = Yup.object().shape({
        name: Yup.string().required(translate('validate.required')),
        skills: Yup.array().of(skillRequirementSchema),
    });

    const submitText = translate(
        `groupForm.${isEdit ? 'editButton' : 'createButton'}`
    );

    return (
        <Formik
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            {...other}
        >
            {({ submitForm, isSubmitting, dirty, values }) => (
                <Box sx={{ pt: { xs: 2, sm: 4 } }}>
                    <ExitPageAlert
                        alert={UnsavedChangesAlert}
                        when={dirty && !isSubmitting}
                        onConfirm={submitForm}
                    />
                    <Typography variant="h3">
                        {isEdit
                            ? values.name
                            : translate('groupForm.createGroup')}
                    </Typography>

                    {isEdit && (
                        <Tabs
                            sx={{
                                mb:
                                    (!isEditable || !canUpdateGroup) &&
                                    activeTab === EGroupFormTab.General
                                        ? 0
                                        : 3,
                            }}
                            value={activeTab}
                        >
                            <Tab
                                label={translate('groupForm.generalTab')}
                                value={EGroupFormTab.General}
                                onClick={() =>
                                    setActiveTab(EGroupFormTab.General)
                                }
                            />

                            <Tab
                                label={translate('groupForm.participantsTab')}
                                value={EGroupFormTab.Participants}
                                onClick={() =>
                                    setActiveTab(EGroupFormTab.Participants)
                                }
                            />
                            {showSkillsTab && (
                                <Tab
                                    label={
                                        <Box
                                            sx={{
                                                alignItems: 'center',
                                                display: 'flex',
                                                gap: 0.5,
                                            }}
                                        >
                                            {translate('groupSkills.tab')}

                                            {group?.compliancy?.isCompliant ===
                                                false && (
                                                <ErrorIcon
                                                    aria-label={translate(
                                                        'groupsManagementPage.compliancyStatus.minimumNotMet'
                                                    )}
                                                    sx={{
                                                        color: 'error.main',
                                                        fontSize: 24,
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    }
                                    value={EGroupFormTab.Skills}
                                    onClick={() =>
                                        setActiveTab(EGroupFormTab.Skills)
                                    }
                                />
                            )}
                            <Tab
                                label={translate('groupForm.groupManagersTab')}
                                value={EGroupFormTab.Managers}
                                onClick={() =>
                                    setActiveTab(EGroupFormTab.Managers)
                                }
                            />
                        </Tabs>
                    )}

                    <Box
                        sx={{
                            mt: isEdit ? 0 : 2,
                            display:
                                activeTab === EGroupFormTab.General
                                    ? 'block'
                                    : 'none',
                        }}
                    >
                        <GroupGeneralForm isEditable={isEditable} />
                    </Box>

                    {isEdit && !!group && (
                        <>
                            <Box
                                sx={{
                                    display:
                                        activeTab === EGroupFormTab.Participants
                                            ? 'block'
                                            : 'none',
                                }}
                            >
                                {group && (
                                    <UsersView
                                        group={group}
                                        onUserUpdate={onUserUpdate}
                                    />
                                )}
                            </Box>

                            {showSkillsTab && (
                                <Box
                                    sx={{
                                        display:
                                            activeTab === EGroupFormTab.Skills
                                                ? 'block'
                                                : 'none',
                                    }}
                                >
                                    <SkillsView group={group} />
                                </Box>
                            )}

                            <Box
                                sx={{
                                    display:
                                        activeTab === EGroupFormTab.Managers
                                            ? 'block'
                                            : 'none',
                                }}
                            >
                                <ManagersView
                                    group={group}
                                    isActive={
                                        activeTab === EGroupFormTab.Managers
                                    }
                                />
                            </Box>
                        </>
                    )}

                    {activeTab === EGroupFormTab.General && canUpdateGroup && (
                        <Box sx={{ display: 'flex', mt: 4 }}>
                            <Button
                                color="primary"
                                disabled={
                                    isSubmitting ||
                                    disabled ||
                                    (!isSubmitting && !disabled && !dirty)
                                }
                                loading={isSubmitting}
                                type="submit"
                                variant="contained"
                                onClick={(
                                    e: React.MouseEvent<HTMLButtonElement>
                                ) => {
                                    e.preventDefault();

                                    return submitForm();
                                }}
                            >
                                {submitText}
                            </Button>

                            {onClose && (
                                <Box sx={{ ml: 2 }}>
                                    <Button
                                        disabled={isSubmitting}
                                        variant="contained"
                                        onClick={onClose}
                                    >
                                        {translate('cancel')}
                                    </Button>
                                </Box>
                            )}

                            {isEditable && isEdit && canUpdateGroup && (
                                <Box sx={{ ml: 'auto' }}>
                                    <FormDeleteButton
                                        disabled={isSubmitting}
                                        onClick={() => onDelete?.()}
                                    >
                                        {translate('delete')}
                                    </FormDeleteButton>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            )}
        </Formik>
    );
};
